<!--
  DesignDetailPage.vue -- the design detail surface on the family model.

  Card: Decoration color + family model (https://trello.com/c/2hcgDM7J)
  Handoff: planning/design-handoffs/handoff_2026-08-23/
           2026-08-23-decoration-family-model/
  ADR 0067 + adr/0067-AMENDMENT-threadflow.md.
  Locked Decisions 4, 8, 10, 11, 13, 14 (partly), 16.

  DUAL-MOUNTED (ADR 0070). One page, two routes:

    * Library:  /distributor/decoration-library/artwork/:artwork_id/design
    * Store:    /distributor/stores/:id/edit/decorations
                  /artwork/:artwork_id/design

  (both are registered inside withPrefix('/distributor', ...) -- an
  unprefixed path renders `public-root` with a clean console and no
  error, which is a particularly quiet way to look broken).

  ADR 0070 -- what the two mounts each own
  =======================================
  "One design detail page, dual-mounted... with the breadcrumb and
  shell differing and the content identical."

  * The LIBRARY owns the design: every size, every method, every
    version, every client, account-wide. Sizes are added, versions
    uploaded and approvals chased across all clients there.
  * The STORE mount owns ONE decision: does this store use this design,
    and is each size cleared for THIS STORE'S client. A store has
    exactly one client (`stores.client_id`, a single nullable FK at
    api/src/models/store.py:804; the one-to-many other side is
    Client.stores at api/src/models/client.py:238), so the size rows
    carry ONE approval column and the page NEVER repeats the
    design-level approvals region -- that region is client-first across
    ALL clients, which is the multi-client answer this mount exists not
    to give.

  How the difference is carried, and why not a mode flag
  =====================================================
  ADR 0070 names its own maintenance risk: "a dual-mounted page must
  not grow store-specific behaviour by accident; the mode flag is a
  maintenance risk."

  So there is no mode. The store context arrives as DATA -- the store's
  client id, which is handed to the SAME selector the Library uses
  ("same selector, filtered", ADR 0070 verbatim) -- plus the labels the
  differing shell needs. A client id is a value a selector consumes; a
  mode string is a licence to add a second behaviour every time someone
  wants one, and the second one is always the one nobody reviews.

  The only things that branch on the mount are the two the ADR says
  differ: the breadcrumb/shell, and the presence of the design-level
  approvals region.

  Three regions (Locked Decision 4)
  =================================
  * LEFT, sticky: the version rail.
  * CENTRE: the readiness strip, then one card per method, each
    listing that method's sizes.
  * BELOW: approvals (client-first accordion), then usage.

  Collapses to one column below 1080px, and the rail stops being
  sticky there.

  Vocabulary
  ==========
  Design / Size / Version on screen. "Family" and "variant" are
  correct in the schema and the API and are banned from the interface
  (ADR 0067) -- a test asserts the rendered text contains neither.

  A method card is a GROUPING and carries no state of its own. It shows
  the method name and a size count; it deliberately has no approval
  readout, because a method-level approval is precisely the widening
  this redesign removes.

  What is NOT here, and why
  =========================
  * **The capability line (Locked Decisions 5-7).** ADR 0068's
    declared version kind has no column; see DesignVersionRail.vue.
  * **The light/dark bracket (Locked Decision 9).**
    `decorations.light_dark_pair_id` is a write-and-echo column and
    the approval-spanning resolver does not exist. A dashed bracket
    labelled "One approval - light + dark" would assert a spanning
    approval the system cannot resolve, so the pairing is surfaced as
    a plain note on the affected rows instead and says outright that
    the approval does not yet resolve across the pair.
  * **The size flyover (Locked Decisions 17-22)**, including the
    verbatim change-request text and the per-client actions. Separate
    unit; the rows are not yet clickable into it.
  * **The preview and backdrop switcher.** Locked Decision 4 puts them
    in the rail. They already exist, fully built, on
    DecorationLibraryArtworkDetail.vue; rebuilding them here would be
    a second mechanism for one concept
    (`docs/design-philosophy.md` principle 2). The rail links across
    to that page rather than duplicating it.
-->
<template>
    <div class="ddp-page">
        <!--
          The shell differs, the content does not (ADR 0070). Exactly
          one of these two renders: the Library's back link, or the
          store's breadcrumb naming the store this design is nested
          under.
        -->
        <button
            v-if="!isStoreMount"
            type="button"
            class="ddp__backlink"
            data-testid="design-detail-back"
            @click="goBack"
        >&larr; Decoration library</button>

        <nav
            v-else
            class="ddp__crumbs"
            data-testid="design-detail-store-breadcrumb"
            aria-label="Breadcrumb"
        >
            <button
                type="button"
                class="ddp__crumb"
                data-testid="design-detail-store-back"
                @click="goBackToStoreDecorations"
            >{{ storeCrumbLabel }}</button>
            <span class="ddp__crumbsep" aria-hidden="true">/</span>
            <span class="ddp__crumbhere">Decorations</span>
        </nav>

        <div
            v-if="store.loading"
            class="ddp__loading"
            data-testid="design-detail-loading"
        >Loading design...</div>

        <!--
          One not-found state for all three causes. The endpoint
          answers 404 identically for not-found, soft-deleted and
          cross-tenant so a probe cannot distinguish them; the page
          must not undo that by wording them differently.
        -->
        <div
            v-else-if="store.notFound"
            class="ddp__empty"
            data-testid="design-detail-notfound"
        >
            <h2 class="ddp__emptytitle">Design not found</h2>
            <p class="ddp__emptybody">
                This design does not exist, or it is not in your library.
            </p>
        </div>

        <div
            v-else-if="store.error"
            class="ddp__empty"
            data-testid="design-detail-error"
        >
            <h2 class="ddp__emptytitle">Could not load this design</h2>
            <p class="ddp__emptybody">{{ store.error }}</p>
        </div>

        <div v-else-if="family" class="ddp" data-testid="design-detail-root">
            <!-- Header. -->
            <header class="ddp__header">
                <div class="ddp__eyebrow">Design</div>
                <h1 class="ddp__name" data-testid="design-detail-name">
                    {{ displayName }}
                </h1>
                <div class="ddp__meta" data-testid="design-detail-clients">
                    {{ clientsText }}
                </div>

                <!--
                  One column is only legible if the page says whose it
                  is. An unlabelled single pill reads as the DESIGN's
                  state, which is the widening ADR 0067 forbids -- the
                  same failure as a design-level approval, arrived at
                  by omission instead of by assertion.
                -->
                <p
                    v-if="isStoreMount"
                    class="ddp__scopenote"
                    data-testid="design-detail-store-scope-note"
                >{{ storeScopeNote }}</p>
            </header>

            <div class="ddp__split">
                <!-- REGION 1: the version rail, sticky. -->
                <aside class="ddp__rail">
                    <DesignVersionRail :family="family" />

                    <!--
                      The preview + backdrop switcher live on the
                      existing artwork detail page rather than being
                      rebuilt here. One mechanism per concept.
                    -->
                    <a
                        class="ddp__raillink"
                        data-testid="design-detail-artwork-link"
                        href="#"
                        @click.prevent="goToArtwork"
                    >Preview and file record</a>
                </aside>

                <div class="ddp__main">
                    <!-- REGION 2: readiness, then the sizes. -->
                    <DesignReadinessStrip :family="family" />

                    <section data-testid="design-detail-sizes">
                        <div class="d-flex align-center">
                            <h2 class="ddp__sectiontitle">Sizes</h2>
                            <HelpIcon
                                audience="owner"
                                slug="decoration-library"
                                heading="digitize-a-size"
                            />
                        </div>
                        <p class="ddp__sectionsub">
                            Every size owns its production file and its
                            approvals. Nothing here is inherited from
                            another size.
                        </p>

                        <div
                            v-for="method in methods"
                            :key="method.method_id"
                            class="ddp__card"
                            :data-testid="`method-card-${method.method_id}`"
                        >
                            <div class="ddp__cardhead">
                                <span
                                    class="ddp__cardtitle"
                                    :data-testid="
                                        `method-card-title-${method.method_id}`
                                    "
                                >{{ methodName(method) }}</span>
                                <span
                                    class="ddp__cardcount"
                                    :data-testid="
                                        `method-card-count-${method.method_id}`
                                    "
                                >{{ sizeCountText(method) }}</span>
                            </div>

                            <!--
                              Locked Decision 9's dashed bracket is NOT
                              built: the approval-spanning resolver
                              does not exist, so a bracket promising
                              "one approval" would be a promise the
                              system cannot keep. The pairing is
                              surfaced as a link with that limitation
                              stated.
                            -->
                            <p
                                v-if="hasPairedSizes(method)"
                                class="ddp__pairnote"
                                :data-testid="
                                    `method-card-pairnote-${method.method_id}`
                                "
                            >
                                Some sizes below are linked as a light/dark
                                pair. Approval does not yet resolve across
                                the pair -- each size is approved on its own.
                            </p>

                            <DesignSizeRow
                                v-for="size in method.sizes"
                                :key="size.id"
                                :family="family"
                                :size="size"
                                :client-id="sizeRowClientId"
                            />
                        </div>
                    </section>

                    <!--
                      REGION 3: approvals, then usage.

                      The approvals region is the design-level,
                      client-first accordion -- ALL clients. ADR 0070:
                      "The page never repeats the design-level
                      approvals region." On the store mount the whole
                      approval answer is the one column on each size
                      row, so this region is absent rather than
                      filtered: a one-client accordion would be a
                      second rendering of the same single fact, one
                      screen apart from the first.
                    -->
                    <section v-if="!isStoreMount" class="ddp__section">
                        <div class="d-flex align-center">
                            <h2 class="ddp__sectiontitle">Approvals</h2>
                            <HelpIcon
                                audience="owner"
                                slug="decoration-library"
                                heading="check-and-give-approvals"
                            />
                        </div>
                        <p class="ddp__sectionsub">
                            {{ approvalsSubtitle }}
                        </p>
                        <ClientApprovalAccordion :family="family" />
                    </section>

                    <section class="ddp__section">
                        <div class="d-flex align-center">
                            <h2 class="ddp__sectiontitle">Where it's used</h2>
                            <HelpIcon
                                audience="owner"
                                slug="decoration-library"
                                heading="see-where-it-is-used"
                            />
                        </div>
                        <p
                            class="ddp__sectionsub"
                            data-testid="design-detail-usage"
                        >{{ usageText }}</p>
                    </section>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { useDecorationFamilyStore } from "@/stores/decorationFamily";

// A client id no client can have. Used ONLY to express "this store has
// no client", which must filter to nothing -- distinct from null, which
// the selector reads as unfiltered. Negative because ids are positive
// serials, so it can never collide with a real one.
const NO_CLIENT = -1;

import { formatArtworkName } from "@/utils/formatArtworkName";

import ClientApprovalAccordion from "./ClientApprovalAccordion.vue";
import DesignReadinessStrip from "./DesignReadinessStrip.vue";
import DesignSizeRow from "./DesignSizeRow.vue";
import DesignVersionRail from "./DesignVersionRail.vue";
import HelpIcon from "@/components/Help/HelpIcon.vue";

export default {
    name: "DesignDetailPage",
    components: {
        HelpIcon,
        ClientApprovalAccordion,
        DesignReadinessStrip,
        DesignSizeRow,
        DesignVersionRail,
    },
    props: {
        // Route param, so a string.
        artwork_id: {
            type: [String, Number],
            required: true,
        },

        // ---- ADR 0070 store-mount context ----------------------------
        // All four default to null, and null everywhere IS the Library
        // mount. There is deliberately no `mode` prop: see the header.

        /**
         * The store's single client (`stores.client_id`). Handed
         * straight to `approvalsForSize` as a filter -- "same selector,
         * filtered".
         *
         * A store whose client is not yet set has null here, and the
         * rows then say "No client yet" per size rather than showing a
         * borrowed column. That is the honest answer: an unattached
         * store has nobody to clear a size for.
         */
        storeClientId: {
            type: [String, Number],
            default: null,
        },
        /** Route param of the store this page is nested under. */
        storeId: {
            type: [String, Number],
            default: null,
        },
        /** Display only -- the breadcrumb's first crumb. */
        storeName: {
            type: String,
            default: "",
        },
        /** Display only -- names whose approval column this is. */
        storeClientName: {
            type: String,
            default: "",
        },
    },
    setup() {
        return { store: useDecorationFamilyStore() };
    },
    computed: {
        /**
         * Which mount this is.
         *
         * Keyed on `storeId`, not on `storeClientId`: a store that has
         * no client yet is still the store mount, and must still get
         * the store shell. Keying on the client id would silently drop
         * an unattached store back to the Library chrome -- inside the
         * store editor, which is a page that looks fine and is lying
         * about where the user is.
         */
        isStoreMount() {
            return this.storeId != null && this.storeId !== "";
        },
        storeCrumbLabel() {
            return this.storeName || "This store";
        },
        /**
         * What the size rows filter by.
         *
         * On the Library mount this is null -- unfiltered, every
         * client, which is the Library's whole job.
         *
         * On the store mount it is the store's client id, and when the
         * store has NO client it is a sentinel that matches nobody
         * rather than null. That distinction is the one that matters:
         * null would mean "unfiltered", so an unattached store would
         * render the full multi-client row underneath chrome that says
         * it is showing one client. That is ADR 0070's rejected
         * "converge" option arrived at by accident, and it looks
         * completely normal on screen.
         *
         * A store with no client can clear no size for anyone, so no
         * column is the honest answer, and the scope note says why.
         */
        sizeRowClientId() {
            if (!this.isStoreMount) return null;
            return this.storeHasClient ? this.storeClientId : NO_CLIENT;
        },
        /**
         * Whether this store HAS a client.
         *
         * Keyed on the id, never on the name: `getCampaignById`
         * returns `client_id` but not the client's display name, so a
         * name-keyed test would report every attached store as
         * unattached -- and would do it in the copy, where it reads as
         * a fact about the data rather than as a bug.
         */
        storeHasClient() {
            return this.storeClientId != null && this.storeClientId !== "";
        },
        /**
         * The store client's display NAME.
         *
         * Resolved from the design's OWN client list by id, not from
         * the store loader: `getCampaignById` returns `client_id` and
         * no client name (api/src/endpoints/campaign.py:1152 selects
         * the id alone), and fetching the client separately would be a
         * second round trip for chrome plus a chance for the name and
         * the id to disagree.
         *
         * The family payload's `clients` array is also the RIGHT
         * source: it is the same record `approvalsForSize` names on the
         * pill, so the scope note and the column it describes cannot
         * word the client differently.
         *
         * An explicit `storeClientName` prop still wins when given --
         * that is the caller stating something it knows -- and an
         * empty result is honest: the note then names no client rather
         * than guessing one.
         */
        resolvedStoreClientName() {
            if (this.storeClientName) return this.storeClientName;
            if (!this.storeHasClient) return "";
            const want = Number(this.storeClientId);
            const match = (this.family?.clients ?? []).find(
                (c) => Number(c.id) === want
            );
            return match?.name ?? "";
        },
        storeScopeNote() {
            if (!this.storeHasClient) {
                // No client on the store. Say so rather than showing an
                // unlabelled empty column, which reads as "nothing
                // outstanding".
                return (
                    "This store has no client yet, so no size can be " +
                    "cleared for one. Approvals are requested per size " +
                    "once a client is attached."
                );
            }
            if (!this.resolvedStoreClientName) {
                // We know THAT there is a client and not what it is
                // called -- the store carries a client this design has
                // never been shown to. Saying so is true; naming a
                // guessed client would not be.
                return (
                    "Approvals below are this store's client's. This " +
                    "design's other clients are managed in the " +
                    "decoration library."
                );
            }
            return (
                `Approvals below are ${this.resolvedStoreClientName}'s, ` +
                "for this store. This design's other clients are " +
                "managed in the decoration library."
            );
        },
        family() {
            return this.store.family;
        },
        methods() {
            const list = this.family?.families;
            return Array.isArray(list) ? list : [];
        },
        // Stored filenames carry a '#<storage-key>' identity suffix that is
        // machinery, never shown to a user. formatArtworkName is the
        // single-source display formatter (DISPLAY ONLY -- never route a
        // mutated copy into identity/lookup). The library index already
        // formats the same field this way.
        displayName() {
            return formatArtworkName(this.family?.filename);
        },
        clientsText() {
            const clients = this.family?.clients ?? [];
            if (!clients.length) return "No client yet";
            return clients.map((c) => c.name).join(", ");
        },
        approvalsSubtitle() {
            if (!(this.family?.clients ?? []).length) {
                return "Requested per size, once a client exists.";
            }
            return (
                "By client, with every size listed. A client approving " +
                "one size has approved that size only."
            );
        },
        usageText() {
            const n = Number(this.family?.rollup?.placements ?? 0);
            if (!n) {
                return (
                    "Not on a product yet. Designs are account-wide -- add " +
                    "this one to a store from that store's Decorations page."
                );
            }
            return `${n} placement${n === 1 ? "" : "s"} across this account's stores.`;
        },
    },
    watch: {
        artwork_id: {
            immediate: true,
            handler(id) {
                this.store.clear();
                if (id != null && id !== "") {
                    this.store.fetchFamily(id);
                }
            },
        },
    },
    beforeUnmount() {
        // Drop the payload so a later mount for another tenant cannot
        // paint this design first.
        this.store.clear();
    },
    methods: {
        methodName(method) {
            // The API may return a null display name for a method row
            // that has been removed. Fall back to the slug, then to an
            // honest placeholder -- never to a guessed method.
            return (
                method.method_display_name ||
                method.method_slug ||
                "Unnamed method"
            );
        },
        sizeCountText(method) {
            const n = (method.sizes ?? []).length;
            return `${n} size${n === 1 ? "" : "s"}`;
        },
        hasPairedSizes(method) {
            return (method.sizes ?? []).some(
                (s) => s.light_dark_pair_id != null
            );
        },
        goBack() {
            this.$router.push({ name: "decoration_library_list" });
        },
        goBackToStoreDecorations() {
            // Back to the section this page hangs off, not to history:
            // the store mount is reachable from the Decorations section
            // and a browser-back would be right only when it was
            // reached that way.
            this.$router.push({
                name: "store_edit",
                params: { id: String(this.storeId), section: "decorations" },
            });
        },
        goToArtwork() {
            this.$router.push({
                name: "library_artwork_detail",
                params: { artwork_id: this.artwork_id },
            });
        },
    },
};
</script>

<style scoped>
.ddp-page {
    padding: 20px 26px 60px;
    max-width: 1240px;
    margin: 0 auto;
}
.ddp__backlink {
    background: none;
    border: 0;
    padding: 0;
    font: inherit;
    font-size: 13px;
    color: var(--tf-primary);
    cursor: pointer;
    margin-bottom: 12px;
}
.ddp__loading,
.ddp__empty {
    padding: 32px 18px;
    background: var(--tf-surface-alt);
    border-radius: var(--tf-radius-md);
    color: var(--tf-fg-3);
}
.ddp__emptytitle {
    margin: 0 0 6px;
    font-size: 18px;
    font-weight: 500;
    color: var(--tf-fg-1);
}
.ddp__emptybody {
    margin: 0;
    font-size: 13px;
}
.ddp__header {
    margin: 12px 0 16px;
}
.ddp__eyebrow {
    font-size: 10.5px;
    font-weight: 600;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    color: var(--tf-fg-4);
}
.ddp__name {
    margin: 3px 0 0;
    font-size: 26px;
    font-weight: 500;
    color: var(--tf-fg-1);
    letter-spacing: -0.3px;
    word-break: break-word;
}
.ddp__meta {
    font-size: 13px;
    color: var(--tf-fg-4);
    margin-top: 5px;
}
.ddp__crumbs {
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: 13px;
    margin-bottom: 12px;
    flex-wrap: wrap;
}
.ddp__crumb {
    background: none;
    border: 0;
    padding: 0;
    font: inherit;
    color: var(--tf-primary);
    cursor: pointer;
}
.ddp__crumbsep {
    color: var(--tf-fg-4);
}
.ddp__crumbhere {
    color: var(--tf-fg-3);
}
.ddp__scopenote {
    margin: 9px 0 0;
    padding: 8px 12px;
    background: var(--tf-surface-alt);
    border: 1px solid var(--tf-border);
    border-radius: var(--tf-radius-sm);
    font-size: 12.5px;
    color: var(--tf-fg-3);
    line-height: 1.5;
}
.ddp__split {
    display: grid;
    grid-template-columns: 250px minmax(0, 1fr);
    gap: 24px;
    align-items: start;
}
.ddp__rail {
    position: sticky;
    top: 0;
    display: flex;
    flex-direction: column;
    gap: 14px;
}
.ddp__raillink {
    font-size: 12.5px;
    color: var(--tf-primary);
    text-decoration: none;
}
.ddp__main {
    min-width: 0;
}
.ddp__section {
    margin-top: 26px;
}
.ddp__sectiontitle {
    margin: 0;
    font-size: 16px;
    font-weight: 500;
    color: var(--tf-fg-1);
}
.ddp__sectionsub {
    margin: 3px 0 12px;
    font-size: 12.5px;
    color: var(--tf-fg-4);
    line-height: 1.5;
}
.ddp__card {
    background: var(--tf-surface);
    border: 1px solid var(--tf-border);
    border-radius: var(--tf-radius-md);
    overflow: hidden;
    margin-bottom: 16px;
}
.ddp__cardhead {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
}
.ddp__cardtitle {
    font-size: 15.5px;
    font-weight: 500;
    color: var(--tf-fg-1);
}
.ddp__cardcount {
    font-size: 12px;
    color: var(--tf-fg-4);
}
.ddp__pairnote {
    margin: 0 16px 12px;
    padding: 9px 12px;
    background: var(--tf-surface-alt);
    border: 1px solid var(--tf-border);
    border-radius: var(--tf-radius-sm);
    font-size: 12px;
    color: var(--tf-fg-3);
    line-height: 1.5;
}

/* Locked Decision 4 -- one column below 1080px, rail unsticks. */
@media (max-width: 1080px) {
    .ddp__split {
        grid-template-columns: minmax(0, 1fr);
    }
    .ddp__rail {
        position: static;
    }
}
</style>
